import React, { useMemo } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getErrorType } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-error-type/index.tsx';
import { IssueAdjustmentsErrorHandler } from '@atlassian/jira-issue-adjustments/src/ui/uim-root-async/error-handler/index.tsx';
import {
	IssueAdjustmentsAsyncOld,
	type IssueAdjustmentsAsyncProps,
} from '@atlassian/jira-issue-adjustments/src/ui/uim-root-async/index.tsx';
import { ISSUE_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { UiModificationsJiraCore } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/ui/ui-modifications-jira-core/index.tsx';
import { GIC_VIEW_TYPE } from '../../common/constants/index.tsx';
import type { Props } from './types.tsx';
import { getSupportedFieldsCount } from './utils/get-supported-fields-count/index.tsx';

const UiModificationsEntryPointLoaded = ({
	issueTypeFieldType,
	modules,
	modalFieldConfig,
	resetFormFieldsCounter,
	selectedIssueType,
	selectedProject,
	triggerPointKey,
}: Props) => {
	const issueAdjustmentsContext = useMemo(() => {
		if (selectedProject && selectedIssueType) {
			return {
				project: {
					id: selectedProject.value,
					key: selectedProject.projectKey,
					type: selectedProject.projectType,
					isSimplified: selectedProject.isSimplified,
				},
				issueType: {
					id: selectedIssueType.value,
					name: selectedIssueType.label,
				},
			};
		}

		return null;
	}, [selectedIssueType, selectedProject]);

	const runtimeProps = useMemo<IssueAdjustmentsAsyncProps['runtimeProps']>(
		() => ({
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			issueAdjustmentsContext: issueAdjustmentsContext!,
			fieldsCount: getSupportedFieldsCount({
				...modalFieldConfig,
				[ISSUE_TYPE]: {
					fieldType: issueTypeFieldType,
				},
			}),
			viewType: GIC_VIEW_TYPE,
			resetFormFieldsCounter,
			modules,
		}),
		[
			issueAdjustmentsContext,
			issueTypeFieldType,
			modalFieldConfig,
			modules,
			resetFormFieldsCounter,
		],
	);

	if (!issueAdjustmentsContext) {
		return null;
	}

	if (fg('uim_tenant_context_as_a_props')) {
		return (
			<UiModificationsJiraCore
				triggerPointKey={triggerPointKey}
				runtimeProps={runtimeProps}
				containerId="uiModifications"
				containerPackageName="jiraUiModificationsViewGic"
			/>
		);
	}

	return (
		<IssueAdjustmentsAsyncOld
			containerId="uiModifications"
			containerPackageName="jiraUiModificationsViewGic"
			runtimeProps={runtimeProps}
			triggerPointKey={triggerPointKey}
		/>
	);
};

export const UiModificationsEntryPoint = (props: Props) => (
	<JSErrorBoundary
		id="uiModifications"
		packageName="jiraUiModificationsViewGic"
		fallback={() => <IssueAdjustmentsErrorHandler />}
		attributes={(error: Error) => ({
			errorType: getErrorType(error),
			viewType: GIC_VIEW_TYPE,
		})}
		sendToPrivacyUnsafeSplunk
	>
		<UiModificationsEntryPointLoaded {...props} />
	</JSErrorBoundary>
);
